import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Carousel, Button, Badge, Spinner } from 'react-bootstrap';
import { HiOutlineLocationMarker, HiOutlineCalendar } from 'react-icons/hi';
import { FaWhatsapp, FaPhoneAlt, FaPlayCircle} from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import './PropertyDetails.css';

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProperties, setRelatedProperties] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // YouTube Helper Functions
  const isYouTubeUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const cleanUrl = url.trim().toLowerCase();
    return (
      cleanUrl.includes('youtube.com') || 
      cleanUrl.includes('youtu.be') ||
      cleanUrl.includes('youtube-nocookie.com')
    );
  };

  const isYouTubeShorts = (url) => {
    if (!url || typeof url !== 'string') return false;
    const cleanUrl = url.trim().toLowerCase();
    return (
      cleanUrl.includes('/shorts/') ||
      cleanUrl.includes('youtube.com/shorts/')
    );
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    
    const cleanUrl = url.trim();
    let videoId = '';
    
    // Check for YouTube Shorts first
    if (isYouTubeShorts(url)) {
      const shortsPattern = /(?:\/shorts\/)([^&\n?#]+)/;
      const match = cleanUrl.match(shortsPattern);
      if (match && match[1]) {
        videoId = match[1];
        return videoId.split('?')[0];
      }
    }
    
    // Check for regular YouTube patterns
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtu\.be\/([^?\n]+)/,
      /youtube\.com\/embed\/([^?\n]+)/,
      /youtube\.com\/v\/([^?\n]+)/
    ];
    
    for (const pattern of patterns) {
      const match = cleanUrl.match(pattern);
      if (match && match[1]) {
        videoId = match[1];
        break;
      }
    }
    
    if (videoId.includes('?')) {
      videoId = videoId.split('?')[0];
    }
    
    return videoId || null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeId(url);
    if (!videoId) return url;
    
    const isShorts = isYouTubeShorts(url);
    const params = new URLSearchParams({
      rel: 0,
      modestbranding: 1,
      controls: 1,
      playsinline: 1,
      enablejsapi: 1,
      ...(isShorts ? {
        autoplay: 1,
        mute: 1,
        loop: 1,
      } : {
        autoplay: 0,
        mute: 0,
        loop: 0,
      }),
      cc_load_policy: 1,
      iv_load_policy: 3,
      origin: window.location.origin
    });
    
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
  };

  // دالة لتحويل روابط Imgur إلى روابط مباشرة
  const convertImgurUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
    }
    
    let cleanUrl = url.trim();
    
    if (cleanUrl.includes('imgur.com')) {
      const match = cleanUrl.match(/imgur\.com\/(\w+)/);
      if (match && match[1]) {
        const imageId = match[1];
        return `https://i.imgur.com/${imageId}.jpg`;
      }
    }
    
    return cleanUrl;
  };

  // دالة لتحليل الحقول JSON
  const parseJSONField = (field) => {
    if (!field) return { ar: '' };
    if (typeof field === 'object') return field;
    try {
      return JSON.parse(field);
    } catch (error) {
      console.error('Error parsing JSON field:', error);
      return { ar: field || '' };
    }
  };

  const fetchRelatedProperties = useCallback(async (type, cityJson) => {
    try {
      const cityObj = parseJSONField(cityJson);
      const cityName = cityObj.ar || '';
      
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('type', type)
        .neq('id', id)
        .limit(3)
        .order('created_at', { ascending: false });
      
      if (!error && data && data.length > 0) {
        const filteredData = data.filter(prop => {
          const propCity = parseJSONField(prop.city);
          return propCity.ar === cityName;
        });
        
        if (filteredData.length > 0) {
          setRelatedProperties(filteredData.slice(0, 3));
        }
      }
      
    } catch (err) {
      console.error('Error fetching related properties:', err);
    }
  }, [id]);

  const fetchProperty = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching property:', error);
        setProperty(null);
        return;
      }
      
      if (data) {
        setProperty(data);
        fetchRelatedProperties(data.type, data.city);
      } else {
        setProperty(null);
      }
      
    } catch (err) {
      console.error('Error:', err);
      setProperty(null);
    } finally {
      setLoading(false);
    }
  }, [id, fetchRelatedProperties]);

  useEffect(() => {
    const originalError = console.error;
    
    console.error = (...args) => {
      if (args.length > 0) {
        const msg = args[0];
        if (typeof msg === 'string' && msg.includes('ResizeObserver')) {
          return;
        }
      }
      originalError.apply(console, args);
    };
    
    window.scrollTo(0, 0);
    fetchProperty();
    
    return () => {
      console.error = originalError;
    };
  }, [fetchProperty]); 

  const typeTranslations = {
    apartment: 'شقة',
    house: 'بيت',
    land: 'أرض',
    office: 'مكتب',
    commercial: 'تجاري'
  };

  const getPropertyImages = () => {
    console.log('Raw images data from DB:', property?.images);
    
    if (!property?.images) {
      console.log('No images property found');
      return ['https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80'];
    }
    
    let imagesArray = [];
    
    if (Array.isArray(property.images)) {
      console.log('Images is array, length:', property.images.length);
      imagesArray = property.images;
    }
    else if (typeof property.images === 'string') {
      try {
        const parsed = JSON.parse(property.images);
        if (Array.isArray(parsed)) {
          imagesArray = parsed;
        }
      } catch (error) {
        console.error('Error parsing images JSON:', error);
      }
    }
    
    if (imagesArray.length === 0) {
      return ['https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80'];
    }
    
    const cleanedImages = imagesArray
      .map(img => {
        if (typeof img !== 'string') return null;
        return convertImgurUrl(img);
      })
      .filter(img => img !== null);
    
    console.log('Cleaned images array:', cleanedImages);
    return cleanedImages.length > 0 ? cleanedImages : ['https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80'];
  };

  const getPropertyTitle = () => {
    if (!property) return 'عقار';
    const titleObj = parseJSONField(property.title);
    return titleObj.ar || 'عقار';
  };

  const getPropertyDescription = () => {
    if (!property) return 'لا يوجد وصف متوفر لهذا العقار.';
    const descObj = parseJSONField(property.description);
    return descObj.ar || 'لا يوجد وصف متوفر لهذا العقار.';
  };

  const getPropertyCity = () => {
    if (!property) return 'غير محدد';
    const cityObj = parseJSONField(property.city);
    return cityObj.ar || 'غير محدد';
  };

  const getPropertyDistrict = () => {
    if (!property) return 'غير محدد';
    const districtObj = parseJSONField(property.district);
    return districtObj.ar || 'غير محدد';
  };

  const handleImageError = (e, imgUrl) => {
    console.error(`Failed to load image: ${imgUrl}`);
    e.target.onerror = null;
    
    if (imgUrl.includes('imgur.com')) {
      const match = imgUrl.match(/imgur\.com\/(\w+)/);
      if (match && match[1]) {
        const imageId = match[1];
        const alternativeUrls = [
          `https://i.imgur.com/${imageId}.jpg`,
          `https://i.imgur.com/${imageId}.png`,
          `https://i.imgur.com/${imageId}.jpeg`,
          `https://imgur.com/${imageId}.jpg`,
          `https://imgur.com/${imageId}.png`
        ];
        
        let currentIndex = 0;
        const tryNextUrl = () => {
          if (currentIndex < alternativeUrls.length) {
            console.log(`Trying alternative URL: ${alternativeUrls[currentIndex]}`);
            e.target.src = alternativeUrls[currentIndex];
            currentIndex++;
          } else {
            e.target.src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
          }
        };
        
        tryNextUrl();
      } else {
        e.target.src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
      }
    } else {
      e.target.src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">جاري تحميل تفاصيل العقار...</p>
      </Container>
    );
  }

  if (!property) {
    return (
      <Container className="text-center py-5">
        <div className="alert alert-danger">العقار غير موجود</div>
        <Button 
          variant="primary" 
          onClick={() => navigate('/')}
          className="mt-3"
        >
          العودة للرئيسية
        </Button>
      </Container>
    );
  }

  const title = getPropertyTitle();
  const description = getPropertyDescription();
  const city = getPropertyCity();
  const district = getPropertyDistrict();
  const images = getPropertyImages();

  return (
    <div className="property-details-wrapper" dir="rtl">
      <div className="fixed-property-header py-3 shadow-sm bg-white border-bottom">
        <Container>
          <Row className="align-items-center">
            <Col md={8} xs={12}>
              <h1 className="h4 fw-bold mb-1">{title}</h1>
              <p className="text-muted small mb-0 d-flex align-items-center gap-1">
                <HiOutlineLocationMarker color="#003591" /> 
                {city}، {district}
              </p>
            </Col>
            <Col md={4} xs={12} className="text-md-start mt-2 mt-md-0">
              <h2 className="fw-bold h3 mb-0">
                {property.ownership === 'sale' 
                  ? `₪${property.price?.toLocaleString() || '0'}` 
                  : `₪${property.price?.toLocaleString() || '0'}/شهر`
                }
              </h2>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt-4">
        <Row className="g-4">
          <Col lg={8}>
            <div className="gallery-main-container shadow-sm mb-4 position-relative rounded overflow-hidden">
              {images.length > 0 ? (
                <Carousel 
                  activeIndex={currentImageIndex}
                  onSelect={(index) => setCurrentImageIndex(index)}
                  interval={null}
                  indicators={true}
                  controls={true}
                >
                  {images.map((img, index) => (
                    <Carousel.Item key={index}>
                      <div 
                        className="property-img-container"
                        style={{
                          width: '100%',
                          height: '500px',
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <img 
                          src={img} 
                          className="d-block mw-100 mh-100" 
                          alt={`عقار ${index + 1}`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }}
                          onError={(e) => handleImageError(e, img)}
                          loading="lazy"
                        />
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              ) : (
                <div 
                  className="property-img-container"
                  style={{
                    width: '100%',
                    height: '500px',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img 
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80"
                    className="d-block mw-100 mh-100"
                    alt="عقار"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              )}
              
              <div className="position-absolute top-0 start-0 m-3" style={{zIndex: 10}}>
                <Badge bg={property.ownership === 'sale' ? 'danger' : 'success'} className="px-3 py-2">
                  {property.ownership === 'sale' ? 'للبيع' : 'للإيجار'}
                </Badge>
              </div>
              
              {images.length > 1 && (
                <div className="position-absolute top-0 end-0 m-3" style={{zIndex: 10}}>
                  <Badge bg="dark" className="px-3 py-2">
                    {currentImageIndex + 1} / {images.length}
                  </Badge>
                </div>
              )}
            </div>

            <div className="content-box-white mb-4">
              <h5 className="fw-bold mb-3 border-bottom pb-2">الوصف</h5>
              <div className="text-muted" style={{ lineHeight: '1.8', whiteSpace: 'pre-line' }}>
                {description}
              </div>
            </div>

            <div className="content-box-white mb-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold m-0">المواصفات</h5>
                <span className="small text-muted d-flex align-items-center gap-1">
                  <HiOutlineCalendar />
                  تاريخ الإضافة: {property.created_at 
                    ? new Date(property.created_at).toLocaleDateString('hr-IL')
                    : 'غير متوفر'}
                </span>
              </div>
              <div className="blue-details-grid">
                <div className="grid-cell">
                  <strong>رقم:</strong> 
                  <span>B-{property.id?.substring(0, 8) || '0000'}</span>
                </div>
                <div className="grid-cell">
                  <strong>المساحة:</strong> 
                  <span>{property.area || '0'} م²</span>
                </div>
                <div className="grid-cell">
                  <strong>غرف النوم:</strong> 
                  <span>{property.bedrooms || '0'}</span>
                </div>
                <div className="grid-cell">
                  <strong>الحمامات:</strong> 
                  <span>{property.bathrooms || '0'}</span>
                </div>
              </div>
            </div>

            {/* YouTube Video Section */}
            {property.video && (
              <div className="content-box-white mb-4">
                <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                  <FaPlayCircle className="text-danger" /> 
                  {isYouTubeShorts(property.video) ? 'فيديو قصير (Shorts)' : 'فيديو'}
                </h5>
                
                {isYouTubeUrl(property.video) ? (
                  <div className={isYouTubeShorts(property.video) ? "shorts-container" : ""}>
                    <div className={`ratio ${isYouTubeShorts(property.video) ? "ratio-9x16" : "ratio-16x9"} rounded overflow-hidden shadow-sm bg-light`}>
                      <iframe
                        src={getYouTubeEmbedUrl(property.video)}
                        title="فيديو العقار على يوتيوب"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        style={{ border: 0 }}
                        className={`youtube-iframe ${isYouTubeShorts(property.video) ? 'shorts-iframe' : ''}`}
                      ></iframe>
                    </div>
                    

                  </div>
                ) : (
                  <div className="ratio ratio-16x9 rounded overflow-hidden shadow-sm bg-light">
                    <video 
                      controls 
                      className="w-100" 
                      preload="metadata"
                    >
                      <source src={property.video} type="video/mp4" />
                      متصفحك لا يدعم تشغيل الفيديو.
                    </video>
                  </div>
                )}
              </div>
            )}

            {relatedProperties.length > 0 && (
              <div className="content-box-white mb-4">
                <h5 className="fw-bold mb-3">عقارات مشابهة</h5>
                <Row className="g-3">
                  {relatedProperties.map((related) => {
                    const relatedTitle = parseJSONField(related.title).ar || 'عقار';
                    const relatedCity = parseJSONField(related.city).ar || '';
                    const relatedImages = Array.isArray(related.images) && related.images.length > 0 
                      ? convertImgurUrl(related.images[0])
                      : 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
                    
                    return (
                      <Col key={related.id} md={6} lg={4}>
                        <div className="border rounded p-3">
                          <div 
                            style={{ 
                              height: '150px', 
                              overflow: 'hidden',
                              backgroundColor: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <img 
                              src={relatedImages} 
                              alt={relatedTitle}
                              className="w-100 h-100"
                              style={{ objectFit: 'cover' }}
                              loading="lazy"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80';
                              }}
                            />
                          </div>
                          <h6 className="fw-bold mb-1 mt-2">{relatedTitle}</h6>
                          <p className="text-muted small mb-2">
                            <HiOutlineLocationMarker size={14} /> {relatedCity}
                          </p>
                          <p className="fw-bold text-primary mb-2">
                            {related.ownership === 'sale' 
                              ? `₪${related.price?.toLocaleString()}` 
                              : `₪${related.price?.toLocaleString()}/شهر`
                            }
                          </p>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="w-100"
                            onClick={() => {
                              navigate(`/property/${related.id}`);
                              window.scrollTo(0, 0);
                            }}
                          >
                            عرض التفاصيل
                          </Button>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}
          </Col>

          <Col lg={4}>
            <div className="sticky-sidebar">
              <div className="content-box-white shadow-sm border p-4">
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div>
                    <h6 className="fw-bold m-0">بيتي للعقارات</h6>
                  </div>
                </div>
                <Button 
                  variant="success" 
                  className="w-100 mb-2 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                  style={{backgroundColor: '#25D366', border: 'none'}}
                  onClick={() => {
                    const phone = property.phone || '+972536233649';
                    const propertyUrl = window.location.href; 
                    const message = `مرحباً، أود الاستفسار عن العقار التالي:

                    📍 ${title}
                    🏙️ ${city} - ${district}
                    💰 ${property.ownership === 'sale' 
                        ? `₪${property.price?.toLocaleString()}` 
                        : `₪${property.price?.toLocaleString()}/شهر`}
                    🔗 ${propertyUrl}

                    هل يمكنك تزويدي بمزيد من المعلومات؟`;

                    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                >
                  <FaWhatsapp size={20} /> مراسلة واتساب
                </Button>
                <Button 
                  style={{ backgroundColor: '#003591', borderColor: '#003591', color: '#fff' }} 
                  className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                  onClick={() => {
                    const phone = property.phone || '+972536233649';
                    window.location.href = `tel:${phone}`;
                  }}
                >
                  <FaPhoneAlt size={18} /> اتصال
                </Button>
                
                <div className="mt-4 pt-4 border-top">
                  <h6 className="fw-bold mb-3">المواصفات</h6>
                  <div className="mb-2">
                    <strong className="text-muted">نوع العقار:</strong>
                    <p className="mb-0">{typeTranslations[property.type] || property.type}</p>
                  </div>
                  <div className="mb-2">
                    <strong className="text-muted">غرف النوم:</strong>
                    <p className="mb-0">{property.bedrooms || 0}</p>
                  </div>
                  <div className="mb-2">
                    <strong className="text-muted">الحمامات:</strong>
                    <p className="mb-0">{property.bathrooms || 0}</p>
                  </div>
                  <div className="mb-2">
                    <strong className="text-muted">المساحة:</strong>
                    <p className="mb-0">{property.area || 0} م²</p>
                  </div>
                  <div className="mb-2">
                    <strong className="text-muted">عدد الصور:</strong>
                    <p className="mb-0">{images.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PropertyDetails;